import { useEffect, useState } from 'react';
import api from '../services/api';
import { wranglerError } from './errors';

export const FINISHED = ['done', 'failed'];
const MAX_FAILURES = 5;

/**
 * A running job's stage, with its place in Alchemer's responses while it reads them (refresh, and
 * import from Wrangler v1.5.0). Pages can grow mid-job if Alchemer needs smaller ones.
 */
export const responsesProgressText = (progress) => {
  const { stage = '', page, pages, fetched, total } = progress || {};
  if (page == null) return stage;
  const n = (x) => (x ?? 0).toLocaleString();
  return `${stage}: page ${n(page)} of ${n(pages)}, ${n(fetched)} of ${n(total)} responses`;
};

/**
 * Follow a Wrangler job until it's done or failed.
 *
 * Polls Ranger (never Wrangler directly) every `interval` ms. Gives up after
 * five failed polls in a row, or at once if the job isn't found.
 * Returns { job, error }; both are null until there's something to show.
 */
const useWranglerJob = (surveyId, jobId, { interval = 2000 } = {}) => {
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setJob(null);
    setError(null);
    if (!jobId) return undefined;

    let stopped = false;
    let timer = null;
    let failures = 0;

    const poll = async () => {
      try {
        const { data } = await api.get(`/surveys/${surveyId}/wrangler/jobs/${jobId}/`);
        if (stopped) return;
        failures = 0;
        setJob(data);
        if (FINISHED.includes(data.status)) return;
      } catch (err) {
        if (stopped) return;
        failures += 1;
        if (err?.response?.status === 404 || failures >= MAX_FAILURES) {
          setError(wranglerError(err));
          return;
        }
      }
      timer = setTimeout(poll, interval);
    };

    poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [surveyId, jobId, interval]);

  return { job, error };
};

export default useWranglerJob;
